/**
 * Navigation-aware window scroll restoration (Strategy A).
 *
 * Feel target: fluid list app — Back lands where you left off; search/filter/status
 * stay in the URL; typing does not jump; changing filter/status starts at top;
 * once restored, never yank the user back down.
 *
 * Restore only on: POP / document back_forward / armed navigateToList.
 * Keys: pathname + normalized search (listUrlState).
 */

import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation, useNavigationType, NavigationType } from "react-router-dom";
import { buildRouteStateKey, normalizeSearchString } from "../utils/listUrlState.js";

const MAX_ENTRIES = 40;
const SS_KEY = "wt:scroll-restore:v1";
const MAX_RESTORE_FRAMES = 90;
const RESTORE_DEADLINE_MS = 8000;
const HEIGHT_TOLERANCE_PX = 12;
const SCROLL_CAPTURE_MS = 100;
const REPLACE_SUPPRESS_MS = 2000;
const STUCK_AT_TOP_PX = 48;

/** @type {Map<string, number>} */
const memory = new Map();

let pendingRestoreKey = null;
/** @type {{ aborted: boolean, cleanup?: Function } | null} */
let restoreAbort = null;
let suppressReplaceTopUntil = 0;
/** Cold Back: keep nudging while stuck at top until height is ready */
let coldRetryY = null;
let coldRetryPath = null;

function touch(key, y) {
  memory.delete(key);
  memory.set(key, y);
  while (memory.size > MAX_ENTRIES) {
    memory.delete(memory.keys().next().value);
  }
  persist();
}

function persist() {
  try {
    sessionStorage.setItem(SS_KEY, JSON.stringify(Object.fromEntries(memory)));
  } catch {
    /* ignore */
  }
}

function hydrate() {
  if (memory.size > 0) return;
  try {
    const raw = sessionStorage.getItem(SS_KEY);
    if (!raw) return;
    const obj = JSON.parse(raw);
    if (!obj || typeof obj !== "object" || Array.isArray(obj)) return;
    for (const [k, v] of Object.entries(obj)) {
      const y = Number(v);
      if (typeof k === "string" && k.length > 0 && Number.isFinite(y) && y >= 0) {
        memory.set(k, y);
      }
    }
    while (memory.size > MAX_ENTRIES) {
      memory.delete(memory.keys().next().value);
    }
  } catch {
    /* ignore */
  }
}

export function saveScrollPosition(pathname, search, y = window.scrollY, opts = {}) {
  hydrate();
  const key = buildRouteStateKey(pathname, search);
  const n = Math.max(0, Number(y) || 0);
  const prev = memory.get(key);
  if (!opts.allowZeroOverwrite && n === 0 && Number.isFinite(prev) && prev > 0) return;
  touch(key, n);
}

export function getScrollPosition(pathname, search) {
  hydrate();
  const y = memory.get(buildRouteStateKey(pathname, search));
  return Number.isFinite(y) ? y : null;
}

export function clearScrollRestorationState() {
  stopRestore();
  pendingRestoreKey = null;
  suppressReplaceTopUntil = 0;
  coldRetryY = null;
  coldRetryPath = null;
  memory.clear();
  try {
    sessionStorage.removeItem(SS_KEY);
  } catch {
    /* ignore */
  }
}

export function requestScrollRestoreForUrl(pathname, search = "") {
  pendingRestoreKey = buildRouteStateKey(pathname, search);
}

export function consumePendingScrollRestore(pathname, search = "") {
  const key = buildRouteStateKey(pathname, search);
  if (pendingRestoreKey && pendingRestoreKey === key) {
    pendingRestoreKey = null;
    return true;
  }
  return false;
}

export function __scrollStoreSize() {
  hydrate();
  return memory.size;
}

export function __scrollStoreKeys() {
  hydrate();
  return [...memory.keys()];
}

function stopRestore() {
  if (restoreAbort) {
    restoreAbort.aborted = true;
    try {
      restoreAbort.cleanup?.();
    } catch {
      /* ignore */
    }
    restoreAbort = null;
  }
}

function releaseToUser() {
  stopRestore();
  coldRetryY = null;
  coldRetryPath = null;
}

function maxScrollY() {
  const doc = document.documentElement;
  const body = document.body;
  const height = Math.max(
    doc?.scrollHeight || 0,
    body?.scrollHeight || 0,
    doc?.offsetHeight || 0,
    body?.offsetHeight || 0,
  );
  return Math.max(0, height - window.innerHeight);
}

function listContextSignature(search) {
  const p = new URLSearchParams(normalizeSearchString(search));
  p.delete("q");
  return p.toString();
}

function parsePathAndSearch(fullUrl) {
  const raw = String(fullUrl || "");
  const q = raw.indexOf("?");
  if (q < 0) return { pathname: raw, search: "" };
  return { pathname: raw.slice(0, q), search: raw.slice(q) };
}

/**
 * One-shot restore. Stops on success, deadline, or user scroll gesture.
 */
export function restoreScrollWhenReady(targetY) {
  stopRestore();
  const y = Math.max(0, Number(targetY) || 0);
  if (y <= 0) {
    window.scrollTo(0, 0);
    return;
  }

  const token = { aborted: false, cleanup: null };
  restoreAbort = token;
  let frames = 0;
  let ro = null;
  let pollTimer = null;
  let deadlineTimer = null;
  let settled = false;
  const started = Date.now();

  const cleanup = () => {
    if (deadlineTimer != null) clearTimeout(deadlineTimer);
    if (pollTimer != null) clearInterval(pollTimer);
    if (ro) {
      try {
        ro.disconnect();
      } catch {
        /* ignore */
      }
    }
    window.removeEventListener("wheel", onUserGesture);
    window.removeEventListener("touchmove", onUserGesture);
    window.removeEventListener("keydown", onKey);
    deadlineTimer = null;
    pollTimer = null;
    ro = null;
    if (restoreAbort === token) restoreAbort = null;
  };
  token.cleanup = cleanup;

  const settleOk = () => {
    settled = true;
    suppressReplaceTopUntil = Date.now() + REPLACE_SUPPRESS_MS;
    coldRetryY = null;
    coldRetryPath = null;
    cleanup();
  };

  const onUserGesture = () => {
    if (token.aborted || settled) return;
    releaseToUser();
    settled = true;
  };

  const onKey = (e) => {
    if (["ArrowUp", "ArrowDown", "PageUp", "PageDown", "Home", "End", " "].includes(e.key)) {
      onUserGesture();
    }
  };

  window.addEventListener("wheel", onUserGesture, { passive: true });
  window.addEventListener("touchmove", onUserGesture, { passive: true });
  window.addEventListener("keydown", onKey);

  const tryApply = () => {
    if (token.aborted || settled) return false;
    const max = maxScrollY();
    const cur = window.scrollY || 0;

    // User moved away after layout could reach target — stop
    if (max >= y - HEIGHT_TOLERANCE_PX && cur > STUCK_AT_TOP_PX && cur < y - 80) {
      releaseToUser();
      settled = true;
      return true;
    }

    window.scrollTo(0, Math.min(y, max));
    const after = window.scrollY || 0;

    // Document claimed enough height but scroll stayed clamped at top (cold reload /
    // transient layout). Keep polling — never settleOk or we kill coldRetry and
    // leave the user at 0 after the real list paints.
    if (y > STUCK_AT_TOP_PX && after <= STUCK_AT_TOP_PX) {
      return false;
    }

    if (max >= y - HEIGHT_TOLERANCE_PX) {
      requestAnimationFrame(() => {
        if (token.aborted || settled) return;
        const max2 = maxScrollY();
        window.scrollTo(0, Math.min(y, max2));
        const cur2 = window.scrollY || 0;
        if (y > STUCK_AT_TOP_PX && cur2 <= STUCK_AT_TOP_PX) {
          // Height collapsed between frames (empty list → paint). Keep restoring.
          requestAnimationFrame(apply);
          return;
        }
        settleOk();
      });
      return true;
    }
    return false;
  };

  const apply = () => {
    if (token.aborted || settled) return;
    frames += 1;
    if (tryApply()) return;
    if (frames < MAX_RESTORE_FRAMES || Date.now() - started < RESTORE_DEADLINE_MS) {
      requestAnimationFrame(apply);
    }
  };

  try {
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => tryApply());
      ro.observe(document.documentElement);
      if (document.body) ro.observe(document.body);
    }
  } catch {
    ro = null;
  }

  pollTimer = window.setInterval(() => tryApply(), 100);
  deadlineTimer = window.setTimeout(() => {
    if (token.aborted || settled) return;
    window.scrollTo(0, Math.min(y, maxScrollY()));
    // Keep coldRetry if still stuck at top so later paint can finish the job
    if ((window.scrollY || 0) <= STUCK_AT_TOP_PX) {
      cleanup();
    } else {
      settleOk();
    }
  }, RESTORE_DEADLINE_MS);

  requestAnimationFrame(apply);
}

if (typeof window !== "undefined") {
  window.__WT_SCROLL__ = {
    getStore: () => {
      hydrate();
      return Object.fromEntries(memory);
    },
    size: __scrollStoreSize,
    isRestoring: () => !!restoreAbort,
  };
}

export default function ScrollManager() {
  const location = useLocation();
  const navType = useNavigationType();
  const prevRef = useRef({
    pathname: location.pathname,
    search: location.search,
    key: location.key,
  });
  const isFirstLayoutRef = useRef(true);

  useEffect(() => {
    const hist = window.history;
    if (!hist || !("scrollRestoration" in hist)) return undefined;
    const prev = hist.scrollRestoration;
    try {
      hist.scrollRestoration = "manual";
    } catch {
      /* ignore */
    }
    return () => {
      try {
        hist.scrollRestoration = prev || "auto";
      } catch {
        /* ignore */
      }
    };
  }, []);

  // Capture scroll while browsing (PUSH often zeros scrollY before layout)
  useEffect(() => {
    let throttle = null;
    let lastY = window.scrollY || 0;

    const flush = () => {
      throttle = null;
      if (lastY <= 0 && (restoreAbort || Date.now() < suppressReplaceTopUntil)) return;
      saveScrollPosition(location.pathname, location.search, lastY, {
        allowZeroOverwrite: lastY > 0,
      });
    };

    const onScroll = () => {
      const y = window.scrollY || 0;
      if (
        y <= 0 &&
        lastY > STUCK_AT_TOP_PX &&
        !restoreAbort &&
        coldRetryY == null
      ) {
        lastY = 0;
        saveScrollPosition(location.pathname, location.search, 0, {
          allowZeroOverwrite: true,
        });
        return;
      }
      lastY = y;
      // User left the top → cancel cold retry (no yank)
      if (y > STUCK_AT_TOP_PX && coldRetryY != null) {
        coldRetryY = null;
        coldRetryPath = null;
      }
      if (throttle != null) return;
      throttle = window.setTimeout(flush, SCROLL_CAPTURE_MS);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (throttle != null) clearTimeout(throttle);
      if (lastY > 0) {
        saveScrollPosition(location.pathname, location.search, lastY, {
          allowZeroOverwrite: true,
        });
      }
    };
  }, [location.pathname, location.search]);

  // Cold Back: nudge once height allows, only while still at top
  useEffect(() => {
    const path = location.pathname;
    const tick = () => {
      if (coldRetryY == null) return;
      if (coldRetryPath && coldRetryPath !== path) return;
      if ((window.scrollY || 0) > STUCK_AT_TOP_PX) {
        coldRetryY = null;
        coldRetryPath = null;
        return;
      }
      const y = coldRetryY;
      const max = maxScrollY();
      if (max < y - HEIGHT_TOLERANCE_PX) return;
      window.scrollTo(0, Math.min(y, max));
      suppressReplaceTopUntil = Date.now() + REPLACE_SUPPRESS_MS;
      coldRetryY = null;
      coldRetryPath = null;
      stopRestore();
    };
    const id = window.setInterval(tick, 80);
    return () => clearInterval(id);
  }, [location.pathname, location.search, location.key]);

  useEffect(() => {
    const onPageHide = () => {
      const y = window.scrollY || 0;
      if (y > 0) {
        saveScrollPosition(location.pathname, location.search, y, {
          allowZeroOverwrite: true,
        });
      }
    };
    window.addEventListener("pagehide", onPageHide);
    return () => window.removeEventListener("pagehide", onPageHide);
  }, [location.pathname, location.search]);

  useLayoutEffect(() => {
    const prev = prevRef.current;
    const nextPath = location.pathname;
    const nextSearch = location.search;
    const firstLayout = isFirstLayoutRef.current;
    isFirstLayoutRef.current = false;

    if (prev.pathname !== nextPath || prev.search !== nextSearch) {
      saveScrollPosition(prev.pathname, prev.search, window.scrollY);
    }

    const pendingListReturn = consumePendingScrollRestore(nextPath, nextSearch);
    let historyPop = false;
    if (navType === NavigationType.Pop) {
      if (!firstLayout) {
        historyPop = true;
      } else {
        try {
          historyPop = performance.getEntriesByType?.("navigation")?.[0]?.type === "back_forward";
        } catch {
          historyPop = false;
        }
      }
    }

    if (historyPop || pendingListReturn) {
      const y = getScrollPosition(nextPath, nextSearch);
      if (y != null && y > 0) {
        coldRetryY = y;
        coldRetryPath = nextPath;
        restoreScrollWhenReady(y);
      }
    } else if (navType === NavigationType.Push) {
      releaseToUser();
      window.scrollTo(0, 0);
    } else if (firstLayout) {
      window.scrollTo(0, 0);
    } else {
      const pathChanged = prev.pathname !== nextPath;
      const ctxChanged =
        listContextSignature(prev.search) !== listContextSignature(nextSearch);
      const restoring =
        !!restoreAbort || (coldRetryY != null && coldRetryPath === nextPath);

      if (restoring) {
        // URL hydration / cold Back retry — keep position
      } else if (pathChanged || ctxChanged) {
        // Filter / status / sort / page → clean new context at top
        const suppress =
          !pathChanged && Date.now() < suppressReplaceTopUntil;
        if (!suppress) {
          releaseToUser();
          window.scrollTo(0, 0);
        }
      }
      // only `q` changed → no jump while typing
    }

    prevRef.current = {
      pathname: nextPath,
      search: nextSearch,
      key: location.key,
    };
  }, [location.pathname, location.search, location.key, navType]);

  return null;
}

export function armScrollRestoreFromListUrl(listUrl) {
  if (!listUrl) return;
  const { pathname, search } = parsePathAndSearch(listUrl);
  if (!pathname.startsWith("/")) return;
  requestScrollRestoreForUrl(pathname, search);
}
